/* =========================================================================
   app.tsx — Top-level app: routes between screens.
   ========================================================================= */

import React, {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useScenario, useHashRoute, ScenarioProvider } from "./state";
import { Sidebar, Header, ROUTE_TITLES, KeyboardHelp } from "./components";
import { ErrorBoundary, ScreenErrorBoundary } from "./error-boundary";
import {
  LazyExecutiveSummary,
  LazyOperatorEconomics,
  LazyDeveloperEconomics,
  LazySensitivities,
  LazyBenchmarks,
  LazyModelChecks,
  LazyReport,
  LazyRiskAnalysis,
  LazyAssumptions,
  LazyBuildings,
  LazyRentEconomics,
  LazyBuilderQuotes,
  LazyComplianceDashboard,
  LazyCompare,
  LazyVersions,
  LazySites,
  LazyFallback,
} from "./ws-perf/lazy-screens";
import { StartGuide } from "./screens-start";
import { CampusTweaks } from "./tweaks";
import { AuthGate } from "./auth-gate";
import { AuthProvider } from "./auth-context";
import { TenureEntrance } from "./tenure-entrance";
import { ToastProvider } from "./toast";
import { OfflineFallback } from "./ws-pwa/offline-fallback";
import { observeWebVitals } from "./ws-perf/perf-metrics";
import { createSwipeHandler } from "./ws-mobile/swipe-router";
import { PullToRefresh } from "./ws-mobile/pull-to-refresh";
import { DealWorkspace } from "./screens-deal";
import { isDoltBackend } from "./dolt-mode";
import { DoltWorkspaceGate, DoltDraftBar, DoltHistory } from "./dolt-workspace";

// e2e/audit-navigation.spec.ts's keyboardRouteOrder is a hand-duplicated copy — keep in sync.
const ROUTE_ORDER = [
  "/",
  "/operator",
  "/developer",
  "/assumptions",
  "/buildings",
  "/sensitivities",
  "/benchmarks",
  "/checks",
  "/compliance",
  "/rent-economics",
  "/quotes",
  "/risk",
  "/compare",
  "/versions",
  "/sites",
  "/report",
  "/deal",
];

export function App() {
  const [route, navigate] = useHashRoute();
  const { active, result, scenarios, results, setActiveId, mcResult, loading } =
    useScenario();
  const [showHelp, setShowHelp] = useState(false);
  const datedRouteGuard = new Set([
    "/",
    "/operator",
    "/developer",
    "/assumptions",
    "/buildings",
    "/sensitivities",
    "/benchmarks",
    "/checks",
    "/compliance",
    "/rent-economics",
    "/quotes",
    "/risk",
    "/report",
  ]);

  // Dated scenarios must never fall through to legacy model screens or their
  // export controls. Compare, versions and sites remain available as governance
  // surfaces; every financial workspace route is replaced with the dated view.
  const isDatedRouteBlocked =
    active.calculationMethod === "dated-v2" && datedRouteGuard.has(route);
  useEffect(() => {
    if (!isDatedRouteBlocked) return;
    globalThis.history.replaceState(null, "", "#/deal");
    globalThis.dispatchEvent(new Event("hashchange"));
  }, [isDatedRouteBlocked]);

  // First-run redirect to the "Start here" guide (/#/start). Fires at most
  // once per app lifetime: this effect's only dependency is `loading`, which
  // starts true and flips false exactly once when the initial persistence
  // load (Supabase or localStorage) resolves — so the check below runs
  // exactly once, using whichever `route` was current at that moment.
  // Guarded so it never hijacks a deep link: only eligible when the
  // *initial* hash was root, and only fires if still on "/" once loading
  // settles. Gated on `!loading` also keeps tests that mock a never-resolving
  // loadPersisted() (src/__tests__/app.test.tsx) inert by construction.
  const initialRouteWasRootRef = useRef(route === "/");
  useEffect(() => {
    if (loading) return;
    if (!initialRouteWasRootRef.current) return;
    if (route !== "/") return;
    try {
      const seen = localStorage.getItem("cls-start-guide-seen");
      const hasScenarioData = !!localStorage.getItem(
        "stratosfyre-scenarios-v1",
      );
      if (!seen && !hasScenarioData) {
        navigate("/start");
      }
    } catch {
      /* private browsing — skip the first-run redirect */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberate one-shot on loading settling, not on every route change
  }, [loading]);

  // Update document title on route change
  useEffect(() => {
    const title = ROUTE_TITLES[route] || "TENURE";
    document.title = title + " \u00b7 TENURE";
  }, [route]);

  // Web Vitals monitoring
  useEffect(() => {
    observeWebVitals((metric) => {
      if (metric.rating === "poor") {
        console.warn("[WebVitals]", metric.name, metric.value);
      }
    });
  }, []);

  // Mobile swipe navigation
  useEffect(() => {
    const handler = createSwipeHandler(
      { threshold: 60, routes: ROUTE_ORDER },
      navigate,
    );
    window.addEventListener("touchstart", handler.onTouchStart, {
      passive: true,
    });
    window.addEventListener("touchend", handler.onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", handler.onTouchStart);
      window.removeEventListener("touchend", handler.onTouchEnd);
    };
  }, [navigate]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable ||
        target.getAttribute("role") === "textbox" ||
        target.getAttribute("role") === "combobox"
      ) {
        return;
      }
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (showHelp && e.key !== "?") return;
      if (e.key === "?") {
        setShowHelp((v) => !v);
        e.preventDefault();
        return;
      }
      const k = e.key.toLowerCase();
      // Scenario switching: 1-4
      if (k >= "1" && k <= "4") {
        const idx = Number.parseInt(k, 10) - 1;
        if (scenarios[idx]) {
          setActiveId(scenarios[idx].id);
          e.preventDefault();
        }
        return;
      }
      // Navigation
      if (k === "p") {
        e.preventDefault();
        (async () => {
          try {
            const { printPdf } = await import("./export-pdf");
            await printPdf(
              active,
              result,
              scenarios,
              results,
              mcResult || undefined,
            );
          } catch (err) {
            console.error("Print failed:", err);
            globalThis.print();
          }
        })();
        return;
      }
      if (k === "e") {
        navigate("/");
        e.preventDefault();
        return;
      }
      if (k === "r") {
        navigate("/report");
        e.preventDefault();
        return;
      }
      if (k === "arrowright" || k === "l") {
        const i = ROUTE_ORDER.indexOf(route);
        if (i >= 0 && i < ROUTE_ORDER.length - 1) {
          navigate(ROUTE_ORDER[i + 1]);
          e.preventDefault();
        }
        return;
      }
      if (k === "arrowleft" || k === "h") {
        const i = ROUTE_ORDER.indexOf(route);
        if (i > 0) {
          navigate(ROUTE_ORDER[i - 1]);
          e.preventDefault();
        }
        return;
      }
    };
    globalThis.addEventListener("keydown", onKey);
    return () => globalThis.removeEventListener("keydown", onKey);
  }, [route, navigate, scenarios, setActiveId, showHelp]);

  // Track route navigation direction for page transition animation
  const prevRouteRef = useRef(route);
  const [direction, setDirection] = useState<"right" | "left" | null>(null);
  useLayoutEffect(() => {
    const prev = prevRouteRef.current;
    if (prev !== route) {
      const p = ROUTE_ORDER.indexOf(prev);
      const c = ROUTE_ORDER.indexOf(route);
      setDirection(p >= 0 && c >= 0 ? (c > p ? "right" : "left") : null);
      prevRouteRef.current = route;
    }
  }, [route]);

  let Screen: React.FC | null = isDatedRouteBlocked ? DealWorkspace : null;
  if (!isDatedRouteBlocked)
    switch (route) {
      case "/start":
        Screen = StartGuide;
        break;
      case "/operator":
        Screen = LazyOperatorEconomics;
        break;
      case "/developer":
        Screen = LazyDeveloperEconomics;
        break;
      case "/assumptions":
        Screen = LazyAssumptions;
        break;
      case "/buildings":
        Screen = LazyBuildings;
        break;
      case "/sensitivities":
        Screen = LazySensitivities;
        break;
      case "/benchmarks":
        Screen = LazyBenchmarks;
        break;
      case "/checks":
        Screen = LazyModelChecks;
        break;
      case "/compliance":
        Screen = LazyComplianceDashboard;
        break;
      case "/risk":
        Screen = LazyRiskAnalysis;
        break;
      case "/rent-economics":
        Screen = LazyRentEconomics;
        break;
      case "/quotes":
        Screen = LazyBuilderQuotes;
        break;
      case "/report":
        Screen = LazyReport;
        break;
      case "/deal":
        Screen = DealWorkspace;
        break;
      case "/compare":
        Screen = LazyCompare;
        break;
      case "/versions":
        Screen = isDoltBackend() ? DoltHistory : LazyVersions;
        break;
      case "/sites":
        Screen = LazySites;
        break;
      default:
        Screen = LazyExecutiveSummary;
    }

  return (
    <PullToRefresh
      onRefresh={async () => {
        window.location.reload();
      }}
    >
      <>
        <input
          type="checkbox"
          id="mobile-nav-toggle"
          data-testid="mobile-nav-checkbox"
          className="mobile-nav-toggle"
          aria-label="Toggle navigation"
        />
        <label
          htmlFor="mobile-nav-toggle"
          className="sidebar-drawer-overlay"
          aria-hidden="true"
        ></label>
        <div className="shell">
          <Sidebar route={route} navigate={navigate} />
          <Header route={route} navigate={navigate} />
          <main
            id="main-content"
            data-testid="route-shell"
            className={`main${direction ? ` page-enter-${direction}` : ""}`}
            key={route /* re-mount to retrigger fade-in */}
          >
            <DoltDraftBar />
            <ScreenErrorBoundary>
              <Suspense fallback={<LazyFallback />}>
                {Screen ? (
                  <Screen />
                ) : (
                  <div className="page">Unknown route</div>
                )}
              </Suspense>
            </ScreenErrorBoundary>
          </main>
          <KeyboardHelp open={showHelp} onClose={() => setShowHelp(false)} />
        </div>
      </>
    </PullToRefresh>
  );
}

export default function Root() {
  return (
    <AuthProvider>
      <TenureEntrance>
        <AuthGate>
          <ErrorBoundary>
            <DoltWorkspaceGate>
              <ScenarioProvider>
                <ToastProvider>
                  <OfflineFallback />
                  <CampusTweaks />
                  <App />
                </ToastProvider>
              </ScenarioProvider>
            </DoltWorkspaceGate>
          </ErrorBoundary>
        </AuthGate>
      </TenureEntrance>
    </AuthProvider>
  );
}
